import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Award, TrendingDown, AlertCircle, ChevronRight, ChevronDown } from "lucide-react";
import { catColor, catLabel, euroPrecise } from "../lib/constants";

/**
 * Mostra per ogni ingrediente con almeno 1 fornitore a listino:
 * - fornitore migliore (verde), prezzo per UM base
 * - se ce n'è più di uno: risparmio % rispetto al peggiore
 *
 * Items è il payload di GET /api/stats/best-supplier
 */
export function BestSupplierSection({ items }) {
    const [showAll, setShowAll] = useState(false);

    const top = useMemo(() => {
        // Ordina per risparmio decrescente, poi alfabetico
        return [...items].sort((a, b) => {
            if (b.savings_pct !== a.savings_pct) return b.savings_pct - a.savings_pct;
            return a.ingredient_name.localeCompare(b.ingredient_name);
        });
    }, [items]);

    if (!items || items.length === 0) {
        return (
            <section className="mb-10 animate-fade-up" data-testid="best-supplier-empty">
                <h2 className="font-display text-2xl text-brand-ink mb-3">Miglior fornitore per ingrediente</h2>
                <div className="bg-paper rounded-xl border border-dashed border-brand-line p-8 text-center">
                    <AlertCircle className="mx-auto text-brand-muted mb-3" size={28} strokeWidth={1.4} />
                    <p className="text-sm text-brand-muted max-w-md mx-auto">
                        Nessun listino fornitori da confrontare. Carica una fattura dalla pagina Acquisti
                        oppure aggiungi prezzi manuali nel{" "}
                        <Link to="/suppliers" className="link-gold underline">listino fornitori</Link>.
                    </p>
                </div>
            </section>
        );
    }

    const visible = showAll ? top : top.slice(0, 6);
    const totalPotentialSavings = top
        .filter((i) => i.savings_pct > 0)
        .reduce((s, i) => s + (i.worst_price - i.best_price), 0);

    return (
        <section className="mb-10 animate-fade-up" data-testid="best-supplier-section">
            <div className="flex items-baseline justify-between mb-4 gap-3 flex-wrap">
                <h2 className="font-display text-2xl text-brand-ink">Miglior fornitore per ingrediente</h2>
                {totalPotentialSavings > 0 && (
                    <div className="text-[12px] text-brand-muted">
                        Risparmio potenziale per unità base totale:{" "}
                        <span className="font-semibold text-brand-success">
                            {euroPrecise(totalPotentialSavings)}
                        </span>
                    </div>
                )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {visible.map((it) => (
                    <BestSupplierCard key={it.ingredient_id} item={it} />
                ))}
            </div>

            {top.length > 6 && (
                <button
                    onClick={() => setShowAll((s) => !s)}
                    data-testid="best-supplier-toggle"
                    className="mt-4 inline-flex items-center gap-1 text-[12px] text-brand-muted hover:text-brand-ink"
                >
                    {showAll ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                    {showAll ? "Mostra meno" : `Mostra tutti (${top.length})`}
                </button>
            )}
        </section>
    );
}

function BestSupplierCard({ item }) {
    const hasComparison = item.suppliers_count > 1 && item.savings_pct > 0;
    return (
        <div
            className="bg-paper rounded-xl border border-brand-line shadow-card p-4 flex items-start gap-3"
            data-testid={`best-supplier-${item.ingredient_id}`}
        >
            <span
                className="h-10 w-1.5 rounded-full shrink-0 mt-1"
                style={{ background: catColor(item.ingredient_category) }}
            />
            <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                        <div className="font-semibold text-brand-ink truncate">{item.ingredient_name}</div>
                        <div className="text-[10px] uppercase tracking-[0.16em] text-brand-muted">
                            {catLabel(item.ingredient_category)}
                        </div>
                    </div>
                    {hasComparison && (
                        <div className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-bold text-brand-success shrink-0">
                            <TrendingDown size={11} /> -{item.savings_pct.toFixed(1)}%
                        </div>
                    )}
                </div>
                <div className="mt-2 flex items-center gap-2 text-[13px]">
                    <Award size={14} className="text-brand-gold shrink-0" />
                    <span className="font-semibold text-brand-ink truncate">
                        {item.best_supplier_name}
                    </span>
                </div>
                <div className="mt-1 text-[12px] text-brand-muted">
                    {euroPrecise(item.best_price)} / {item.ingredient_unit}
                    {hasComparison && (
                        <span className="ml-2 line-through opacity-70">
                            {euroPrecise(item.worst_price)}
                        </span>
                    )}
                    {item.suppliers_count > 1 && !hasComparison && (
                        <span className="ml-2">· {item.suppliers_count} fornitori a pari prezzo</span>
                    )}
                </div>
            </div>
        </div>
    );
}
